import { ILocation, IUser } from 'interfaces';
import { LikeCounterVariants } from 'services/rates';

type ICategory = {
  id: number;
  title: string;
  iconLink: string;
};

export type ICompany = {
  address: string;
  description: string;
  id: number;
  link?: string;
  phone: string;
  title: string;
};

export type CompanyDTO = Omit<ICompany, 'id'>;

export type BaseDiscountContent = {
  id: number;
  type: string;
  sizeMax: number;
  sizeMin: number;
  dateBegin: string;
  imageDiscount: string | null;
  likeCount: number;
  dislikeCount: number;
  rateType: LikeCounterVariants;
  discountName?: string;
  isArchived: boolean;
  averageRating?: number | null;
  ratingCount?: number | null;
  userRating?: number | null;
  ratingComment?: string | null;
};
export enum DiscountType {
  OTHER = 'OTHER',
  PROMOCODE = 'PROMOCODE',
  SHOW_AT_LOCATION = 'SHOW_AT_LOCATION',
}

export enum DiscountStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_EXTENDED = 'NOT_EXTENDED',
  DELETED = 'DELETED',
}

export enum DiscountAction {
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_EXTENDED = 'NOT_EXTENDED',
  RESTORE = 'RESTORE',
  DELETE = 'DELETE',
}

export enum DiscountStatusTransitionType {
  REASON_FOR_NON_EXTENSION = 'REASON_FOR_NON_EXTENSION',
  REASON_FOR_EXTENSION = 'REASON_FOR_EXTENSION',
}

export type PatchDiscountStatusBody = {
  newStatus: DiscountStatus;
  editVersion: number;
  comment?: string;
};

export type PatchRestoreFromArchiveBody = {
  editVersion: number;
};

export type DiscountComment = {
  commentId: number;
  text: string;
  timeCreate: string;
  userLogin: string;
};

export type DiscountStatusComment = {
  id: number;
  discountId: number;
  commentText: string;
  transitionType: DiscountStatusTransitionType;
  createdById: number;
  createdByLogin: string;
  createdAt: string;
};

export type ISubcategory = {
  id: number;
  title: string;
  categoryId: number;
  isActive?: boolean;
};

export type IDiscountsContent = BaseDiscountContent & {
  timeCreate: string | null;
  dateFinish: string | null;
  discountCondition: string;
  shortDiscountCondition: string;
  discountType: DiscountType;
  company: ICompany;
  categories: ICategory[];
  subcategory?: ISubcategory | null;
  editVersion?: number;
  area?: ILocation[];
  areaIds?: number[];
  comments: DiscountComment[];
  status?: DiscountStatus;
  statusComments?: DiscountStatusComment[];
  creatorUser?: IUser;
  locationId?: Array<number | { id: number }>;
};

export type IDiscountPreview = {
  companyName?: string;
  shortDiscountCondition?: string;
  discountMin?: number;
  discountMax?: number;
  discountType?: string;
};

export type DiscountDTO = Omit<
  IDiscountsContent,
  | 'id'
  | 'company'
  | 'subcategory'
  | 'likeCount'
  | 'dislikeCount'
  | 'rateType'
  | 'comments'
  | 'timeCreate'
  | 'creatorUser'
  | 'averageRating'
  | 'userRating'
  | 'ratingComment'
> & {
  companyId?: number;
  subcategoryId?: number;
  editVersion?: number;
  isArchived: boolean;
};

export type ShortDiscountContent = Omit<
  BaseDiscountContent,
  'imageDiscount'
> & {
  image: null | string;
  companyName: string;
  subcategory?: ISubcategory | null;
};

export type IDiscountResponse = {
  content: IDiscountsContent[];
  numberOfElements: number;
  empty: boolean;
};

export type ISimilarDiscountRequest = {
  locationId: number;
  categoryId: number;
  discountId: number;
  limit: number;
  userId: number;
};

export type ISimilarDiscount = ShortDiscountContent & {
  cityId: number;
  city: string;
};

export type ISimilarDiscountResponse = {
  category: ICategory;
  discounts: ISimilarDiscount[];
};

export type ISimilarCheckDiscountResponse = {
  discountId: number;
  companyName: string;
  discountType: DiscountType;
};

export type SimilarityType = 'similar' | 'overlap';

export type IExistingDiscount = ISimilarCheckDiscountResponse & {
  similarityType: SimilarityType;
};

export enum DiscountRangeType {
  FIX = 'FIX',
  RANGE = 'RANGE',
  OTHER = 'OTHER',
}

export type IDiscounts = {
  content: IDiscountsContent[];
  totalPages: number;
  totalElements: number;
};

export type IDiscountForMainPage = {
  category: ICategory;
  discounts: ShortDiscountContent[];
};

export type IDuplicate = ISimilarCheckDiscountResponse &
  Pick<BaseDiscountContent, 'sizeMax' | 'sizeMin'>;

export type IDiscountEditorNavigationState = {
  editDiscountId: number | null;
  restoreFromArchive?: boolean;
};

export type IDiscountPageNavigationState = {
  restoredFromArchive?: boolean;
};

export type IGetDiscountsForMainPage = {
  cityId: number;
  userId: number;
  discountsPerCategory?: number;
};

export type IGetLatestDiscountsForMainPage = {
  countryName: string;
  cityName: string;
  page: number;
  size: number;
};

export type IGetDiscountByCategoryParams = {
  categoryId: number;
  sort: string;
  direction: string;
  page: number;
  city: string;
  country: string;
  size: number;
};

export type GetDiscountsFilterParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'ASC' | 'DESC';
  country: string | null;
  city: string | null;
  categoryId: number | null;
  creatorUserId: number | null;
  dateStart: string | null;
  dateEnd: string | null;
};

export type DownloadDiscountsByXlsxParams = {
  country: string | null;
  city: string | null;
  categoryId: number | null;
  creatorUserId: number | null;
  dateStart: string | null;
  dateEnd: string | null;
};

export type GetTopDiscountsParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: string;
};

export type GetUsersParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'ASC' | 'DESC';
  totalClicksCount?: string | null;
};

export type ITopDiscountsContent = {
  discountId: number;
  companyTitle: string;
  country: string;
  city?: string;
  clicksCount: number;
};

export type ITopDiscounts = {
  content: ITopDiscountsContent[];
  totalPages: number;
  totalElements: number;
};

export type ITopUser = {
  userId: number;
  login: string;
  totalClicksCount: number;
};
